/* Dialog for creating a new tag-based goal with name, tags, and daily time target. */

import { useState, useCallback, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { SourceTagSelect } from "@/components/sources/SourceTagSelect";
import { GoalIconPicker } from "@/components/goals/GoalIconPicker";
import {
  GOAL_TIME_OPTIONS,
  formatMinutes,
  parseTimeInput,
  validateGoalMinutes,
} from "@/components/goals/goalTimeOptions";

interface CreateGoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateGoalDialog({ open, onOpenChange }: CreateGoalDialogProps) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("\u{1F3AF}");
  const [tags, setTags] = useState<string[]>([]);
  const [timeInput, setTimeInput] = useState("1h");
  const [timeError, setTimeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createGoal = useMutation(api.tagGoals.create);
  const availableTags = useQuery(api.tags.getAvailableTags) ?? [];
  const addTag = useMutation(api.tags.addTag);

  const portalTargetRef = useRef<HTMLDivElement>(null);

  const resetForm = useCallback(() => {
    setName("");
    setIcon("\u{1F3AF}");
    setTags([]);
    setTimeInput("1h");
    setTimeError(null);
    setIsSubmitting(false);
    setError(null);
  }, []);

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      resetForm();
    }
  }

  function handleTimeInputChange(value: string) {
    setTimeInput(value);
    setTimeError(null);
  }

  function handlePresetClick(minutes: number) {
    setTimeInput(formatMinutes(minutes));
    setTimeError(null);
  }

  function resolveMinutes(): number | null {
    const parsed = parseTimeInput(timeInput);
    if (parsed === null) {
      setTimeError("Enter a value like 30m, 1.5h, or 90");
      return null;
    }
    const validationError = validateGoalMinutes(parsed);
    if (validationError) {
      setTimeError(validationError);
      return null;
    }
    return parsed;
  }

  async function handleCreateTag(tag: string) {
    await addTag({ tag });
  }

  async function handleSubmit() {
    if (!name.trim() || tags.length === 0) return;

    const minutes = resolveMinutes();
    if (minutes === null) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await createGoal({
        name: name.trim(),
        tags,
        icon,
        dailyGoalMinutes: minutes,
      });
      handleOpenChange(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create goal";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  const canSubmit =
    name.trim().length > 0 && tags.length > 0 && !isSubmitting;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <div ref={portalTargetRef} className="absolute" />
        <DialogHeader>
          <DialogTitle>New Goal</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="goal-name">Name</Label>
            <div className="flex gap-2">
              <GoalIconPicker value={icon} onChange={setIcon} />
              <Input
                id="goal-name"
                placeholder="e.g. Learn Spanish"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tags</Label>
            <SourceTagSelect
              selectedTags={tags}
              onTagsChange={setTags}
              availableTags={availableTags}
              onCreateTag={handleCreateTag}
              portalContainer={portalTargetRef}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="goal-time">Duration</Label>
            <Input
              id="goal-time"
              placeholder="e.g. 1h, 30m, 90"
              value={timeInput}
              onChange={(e) => handleTimeInputChange(e.target.value)}
              autoComplete="off"
            />
            <div className="flex flex-wrap gap-1.5">
              {GOAL_TIME_OPTIONS.map((opt) => (
                <button
                  key={opt.minutes}
                  type="button"
                  onClick={() => handlePresetClick(opt.minutes)}
                  className="rounded-md border px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {timeError && (
              <p className="text-destructive text-sm">{timeError}</p>
            )}
          </div>

          {error && (
            <p className="text-destructive text-sm">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && (
              <Loader2 className="size-4 animate-spin" />
            )}
            Create Goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
